const BASE_URL = import.meta.env.MODE === 'production'
  ? '' // 生产环境使用相对路径（同源代理）
  : (import.meta.env.VITE_API_URL || 'http://localhost:3000'); // 开发环境，走 nginx 代理

export class ApiError extends Error {
  constructor(
    public status: number,
    public body: any
  ) {
    super(body?.message || `API Error: ${status}`);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T = any>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  // 动态导入避免循环依赖
  const { useAuthStore } = await import('./stores/auth');
  const token = useAuthStore.getState().token;
  
  const headers: Record<string, string> = {
    ...(options.headers as Record<string, string> || {}),
  };
  // 仅在有请求体时声明 JSON Content-Type。Fastify 5 对声明 application/json
  // 但 body 为空的请求（如无 body 的 DELETE/GET）会返回 400:
  // "Body cannot be empty when content-type is set to 'application/json'"。
  const hasBody = options.body !== undefined && options.body !== null;
  if (hasBody && !headers['Content-Type'] && !headers['content-type']) {
    headers['Content-Type'] = 'application/json';
  }
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  const url = path.startsWith('http') ? path : `${BASE_URL}${path}`;
  
  const response = await fetch(url, {
    ...options,
    headers,
    credentials: 'include', // 携带 httpOnly cookie
  });
  
  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    
    // 401 未授权 → 清除 token 并跳转登录页
    if (response.status === 401) {
      const { useAuthStore } = await import('./stores/auth');
      useAuthStore.getState().logout();
      window.location.href = '/login';
    }
    
    throw new ApiError(response.status, body);
  }
  
  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }
  
  return response.json();
}

// 封装常用 HTTP 方法
export const api = {
  get: <T = any>(path: string) => apiFetch<T>(path),
  
  post: <T = any>(path: string, body?: any) =>
    apiFetch<T>(path, {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    }),
  
  put: <T = any>(path: string, body?: any) =>
    apiFetch<T>(path, {
      method: 'PUT',
      body: body ? JSON.stringify(body) : undefined,
    }),
  
  patch: <T = any>(path: string, body?: any) =>
    apiFetch<T>(path, {
      method: 'PATCH',
      body: body ? JSON.stringify(body) : undefined,
    }),

  delete: <T = any>(path: string) =>
    apiFetch<T>(path, { method: 'DELETE' }),
};
